// @ts-nocheck
import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import Icon from 'customer-flows/components/ui-kit/icon'
import ValueInspector from './value-inspector'
import { service } from '@ember/service'
import { getOwner } from '@ember/owner'
import type Owner from '@ember/owner'

const serviceModules = import.meta.glob('../../../services/**/*.ts', { eager: true })
const SERVICE_NAMES: string[] = Object.entries(serviceModules)
  .filter(([, mod]) => (mod as any).default != null)  // abstract/base classes have no default export
  .map(([path]) => path.replace('../../../services/', '').replace('.ts', ''))

export default class ServiceInspectorComponent extends Component {
  @service declare session: any

  @tracked expandedSections: Set<string> = new Set()

  constructor(owner: Owner, args: object) {
    super(owner, args)
    const saved = this.session.restore()['debugToolbarExpandedServices']
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        this.expandedSections = new Set(parsed)
      } catch (e) {
        // ignore parse errors
      }
    }
  }

  @action
  toggleSection(section: string) {
    if (this.expandedSections.has(section)) {
      this.expandedSections.delete(section)
    } else {
      this.expandedSections.add(section)
    }
    this.expandedSections = new Set(this.expandedSections)
    this.session.persist({
      debugToolbarExpandedServices: JSON.stringify(Array.from(this.expandedSections)),
    })
  }

  isExpanded = (name: string) => this.expandedSections.has(name)

  getIcon = (name: string) => (this.expandedSections.has(name) ? 'carat-down' : 'carat-forward')

  formatName = (name: string) =>
    name
      .split('/')
      .map((part) =>
        part
          .split('-')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ')
      )
      .join(' / ')

  isInjectedService = (value: any) => {
    if (!value || typeof value !== 'object') return false
    const constructorName = value.constructor?.name || ''
    return (
      constructorName.endsWith('Service') ||
      constructorName.endsWith('Manager') ||
      constructorName.endsWith('Router') ||
      constructorName.endsWith('Builder') ||
      constructorName.endsWith('Tracker') ||
      constructorName.endsWith('Logger') ||
      constructorName === 'Owner' ||
      value.isService === true
    )
  }

  getServiceData = (serviceName: string) => {
    try {
      const owner = getOwner(this)
      const svc = owner.lookup(`service:${serviceName}`)
      if (!svc) return { error: 'Not registered' }

      const data: Record<string, any> = {}
      const allKeys = new Set<string>()

      let obj = svc
      while (obj && obj !== Object.prototype) {
        Object.getOwnPropertyNames(obj).forEach((key) => allKeys.add(key))
        const descriptors = Object.getOwnPropertyDescriptors(obj)
        Object.keys(descriptors).forEach((key) => {
          if (descriptors[key].get) allKeys.add(key)
        })
        obj = Object.getPrototypeOf(obj)
      }

      for (const key of allKeys) {
        if (key.startsWith('_') || key === 'constructor' || key === 'isDestroyed' || key === 'isDestroying') continue
        try {
          const value = svc[key]
          if (typeof value === 'function') continue
          if (this.isInjectedService(value)) continue
          if (value === null || value === undefined) {
            data[key] = value
          } else if (typeof value === 'object') {
            if (value.constructor?.name === 'Map') {
              data[key] = Array.from(value.entries()).reduce((acc, [k, v]) => {
                acc[k] = v
                return acc
              }, {})
            } else if (value.constructor?.name === 'Set') {
              data[key] = Array.from(value)
            } else if (value.value !== undefined && value.state !== undefined) {
              data[key] = value.value
            } else {
              data[key] = value
            }
          } else {
            data[key] = value
          }
        } catch (e) {
          data[key] = `Error: ${e.message}`
        }
      }

      return data
    } catch (e) {
      return { error: e.message }
    }
  }

  <template>
    <div data-testid='service-inspector' class='flex h-full w-full flex-col overflow-auto p-4'>
      <h2 class='text-xbold-para-16 mb-3 text-neutrals-400'>Services</h2>

      <div class='flex flex-col gap-3'>
        {{#each SERVICE_NAMES as |serviceName|}}
          <div class='rounded-3 overflow-hidden border border-neutrals-100 bg-neutrals-50'>
            <div
              class='flex cursor-pointer items-center justify-between bg-theme-50 px-4 py-3 transition-colors hover:bg-theme-100'
              {{on 'click' (fn this.toggleSection serviceName)}}
            >
              <span class='text-bold-para-14 text-neutrals-400'>{{this.formatName serviceName}}</span>
              <Icon @src={{this.getIcon serviceName}} class='h-4 w-4' />
            </div>
            {{#if (this.isExpanded serviceName)}}
              <div class='flex flex-col gap-2 p-4'>
                {{#each-in (this.getServiceData serviceName) as |key value|}}
                  <div class='flex flex-col gap-1'>
                    <span class='text-bold-para-12 text-neutrals-400'>{{key}}</span>
                    <ValueInspector @value={{value}} />
                  </div>
                {{/each-in}}
              </div>
            {{/if}}
          </div>
        {{/each}}
      </div>
    </div>
  </template>
}
